import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../ui/Button";
import { Card, CardBody, CardHeader } from "../ui/Card";
import Input from "../ui/Input";
import { API_BASE_URL, apiFetch, setStoredToken } from "../api/client";

const AUTH_KEY = "mockAuth";
const USER_EMAIL_KEY = "estimation_station_user_email";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState("");

  React.useEffect(() => {
    const token = String(searchParams.get("google_token") || "").trim();
    if (!token) return;
    setStoredToken(token);
    localStorage.setItem(AUTH_KEY, "true");
    localStorage.setItem("auth_provider", "google");
    const next = String(searchParams.get("next") || "").trim();
    navigate(next || "/app/build", { replace: true });
  }, [navigate, searchParams]);

  const handleEmailLogin = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const out = await apiFetch("/auth/login", {
        method: "POST",
        body: {
          email: String(email || "").trim(),
          password: String(password || ""),
        },
      });
      const token = String(out?.data?.access_token || out?.data?.token || "").trim();
      if (!token) {
        throw new Error("Login response missing access token");
      }
      setStoredToken(token);
      localStorage.setItem(AUTH_KEY, "true");
      localStorage.setItem("auth_provider", "email");
      localStorage.setItem(USER_EMAIL_KEY, String(email || "").trim().toLowerCase());
      const next = String(searchParams.get("next") || "").trim();
      navigate(next || "/app/build");
    } catch (e) {
      setError(e?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await apiFetch("/auth/register", {
        method: "POST",
        body: {
          email: String(email || "").trim(),
          password: String(password || ""),
        },
      });
      setNotice("Account created. Signing in...");
      await handleEmailLogin();
    } catch (e) {
      if (String(e?.message || "").includes("409")) {
        setError("Account already exists. Use Email Login.");
      } else {
        setError(e?.message || "Registration failed");
      }
      setBusy(false);
    }
  };

  const handleGoogleLogin = () => {
    const next = String(searchParams.get("next") || "").trim() || "/app/build";
    const oauthUrl =
      String(process.env.REACT_APP_GOOGLE_AUTH_URL || "").trim() ||
      `${API_BASE_URL}/auth/google/start?next=${encodeURIComponent(next)}`;
    if (oauthUrl) {
      window.location.assign(oauthUrl);
      return;
    }
    setError("Google login is not configured in this environment.");
  };

  return (
    <main className="mx-auto flex min-h-[calc(100vh-2rem)] max-w-md items-center px-4 py-8">
      <Card className="w-full">
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Login</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to continue to Estimation Station.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          {notice ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</p> : null}
          <Button className="w-full" onClick={handleEmailLogin} disabled={busy}>
            {busy ? "Signing in..." : "Email Login"}
          </Button>
          <Button variant="secondary" className="w-full" onClick={handleRegister} disabled={busy}>
            {busy ? "Please wait..." : "Register"}
          </Button>
          <Button variant="secondary" className="w-full" onClick={handleGoogleLogin}>
            Continue with Google
          </Button>
          <Link to="/pricing" className="text-xs text-slate-500 underline">
            View pricing
          </Link>
          <Link to="/" className="text-xs text-slate-500 underline">
            Back to home
          </Link>
        </CardBody>
      </Card>
    </main>
  );
}
